import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, interval, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import {
  CancelRefundAction,
  CancelRefundErrorBody,
  CancelRefundHold,
  CancelRefundItem,
  CancelRefundOptions,
  CancelRefundReason,
  CancelRefundReasonPrompt,
  CancelRefundRequest,
  CancelRefundResult,
  CancelRefundService,
  CancelTiming,
  FamilyCancelMode,
  FamilyCancelOptions,
  FamilyCancelPlate,
  FamilyCancelRequest,
  FamilyCancelResult,
  RefundOption,
} from '../../../services/cancel-refund.service';
import {
  CancelRefundIntent,
  ERROR_COPY,
  REASON_NOTE_MAX,
  REASON_REQUIRING_NOTE,
  SelectionState,
  actionFor,
  amountForChoice,
  buildRequest,
  defaultOption,
  formatCents,
  formatCountdown,
  formatDate,
  holdRemainingSeconds,
  newRequestId,
  promptFor,
  proratedBasisLabel,
  reasonsFor,
  refundDestinationLabel,
  sameOffer,
  targetAmounts,
  targetItems,
  targetLabel,
  targetOptions,
  unavailableCopy,
  validateSelection,
} from './cancel-refund-panel.logic';
import {
  FamilySelection,
  buildFamilyRequest,
  endingPlates,
  familyConfirmLabel,
  familyDoneResult,
  familyNeedsCash,
  familyRefundTotal,
  familySummary,
  familyModeTiles,
  familyUpdatePending,
  defaultNewPrimary,
  familyModeBlockReason,
  newPrimaryOptions,
  FamilyModeTile,
  NewPrimaryOption,
  reconcileFamilySelection,
  isFamilyPrimaryCancel,
  needsNewPrimary,
  plateLabel,
  plateOptionLabels,
  plateRefund,
  plateRoleLabel,
  platesInScope,
  primaryOnlyNote,
  validateFamily,
} from './cancel-refund-panel.family-logic';

export type CancelRefundPanelState = 'loading' | 'unavailable' | 'select' | 'processing';

/** What the host gets once a cancellation ran; the host closes the panel and tells the operator. */
export interface CancelRefundDone {
  result: CancelRefundResult;
  orderId: string;
  /** The panel's own one-line summary, for the host's toast. */
  summary: string;
  /** CWS-1579: the full answer when the panel cancelled a family primary. */
  familyResult?: FamilyCancelResult;
}

const FAMILY_MODES: FamilyCancelMode[] = ['all', 'primary_only'];

const OPTION_LABELS: Record<RefundOption, string> = {
  none: 'No refund',
  prorated: 'Prorated refund',
  full: 'Full refund',
  custom: 'Custom amount',
};

/**
 * CWS-1778 (story CWS-1534): the cancel & refund modal from the Orders Refund
 * Flow design, shared by the Orders row menu (CWS-1779), the lane pop-up and
 * the Customers tab (CWS-1780).
 *
 * One target per opening (the menu item tapped), one refund choice, one
 * reason, Confirm. On success the modal closes and the host refreshes the row —
 * the design has no result screen. Every amount shown comes from the server;
 * the panel formats and forwards.
 */
@Component({
  selector: 'app-cancel-refund-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cancel-refund-panel.component.html',
  styleUrls: ['./cancel-refund-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CancelRefundPanelComponent implements OnChanges, OnDestroy {
  private readonly cancelRefundService = inject(CancelRefundService);
  private readonly zone = inject(NgZone);
  private readonly destroy$ = new Subject<void>();
  /** Fires on every load: a late answer for the previous target must never land on this one. */
  private readonly reload$ = new Subject<void>();

  /** Loads on the transition to true, like the other modals. */
  @Input() isOpen = false;
  /** One of the two names the purchase; an order row passes the order. */
  @Input() orderId: string | null = null;
  @Input() subscriptionId: string | null = null;
  @Input() locationId: string | null = null;
  /** Which menu item opened the panel; picks the target items. */
  @Input() intent: CancelRefundIntent = 'cancel';
  /** The subtitle: "Order W-10486 · Maria Lopez · Oct 19, 2026, 10:42 AM". */
  @Input() orderNumber: string | null = null;
  @Input() customerName: string | null = null;
  @Input() orderDate: string | null = null;

  @Output() closed = new EventEmitter<void>();
  /** Emitted once, when a cancellation was executed (whatever its refund outcome). */
  @Output() done = new EventEmitter<CancelRefundDone>();

  state: CancelRefundPanelState = 'loading';
  options: CancelRefundOptions | null = null;
  items: CancelRefundItem[] = [];
  /** Why the panel cannot be used (load refused) or why the last Confirm failed. */
  errorMessage: string | null = null;

  selection: SelectionState = {
    choice: { option: 'none', customInput: '' },
    reasonCode: null,
    reasonNote: '',
    cashHandedOver: false,
    cancelTiming: 'period_end',
  };

  /**
   * CWS-1579: set when the panel cancels a family primary with members. The panel then offers
   * Cancel all / Cancel primary only and a refund per plate instead of the single item card.
   */
  family: FamilyCancelOptions | null = null;
  familySelection: FamilySelection = { mode: null, newPrimaryMemberId: null, choices: {} };
  /** Sent again unchanged when the plates were cancelled but the family update did not finish. */
  private pendingFamilyBody: FamilyCancelRequest | null = null;

  /** The same for a plain cancel of a family member (CWS-1579). */
  private pendingBody: CancelRefundRequest | null = null;

  /**
   * A request waiting to be finished is resent exactly as it was, so what is on screen is frozen
   * until it goes through.
   */
  get locked(): boolean {
    return !!(this.pendingFamilyBody || this.pendingBody);
  }

  get inputsLocked(): boolean {
    return this.state === 'processing' || this.locked;
  }

  /**
   * CWS-1947: this panel's claim on the order while the operator decides.
   *
   * Null is a working state, not a failure: if the hold could not be taken the
   * panel behaves exactly as it did before, and the server still arbitrates on
   * Confirm. Only the countdown depends on having one.
   */
  hold: CancelRefundHold | null = null;
  holdSecondsLeft = 0;
  /** Stops the ticker without tearing down the load subscriptions. */
  private readonly holdStopped$ = new Subject<void>();

  readonly noteMax = REASON_NOTE_MAX;
  readonly optionLabels = OPTION_LABELS;
  private requestId = newRequestId();
  /** CWS-1846: one silent reload-and-resend per Confirm when only the version token moved. */
  private staleRetried = false;

  ngOnChanges(changes: SimpleChanges): void {
    const opened = changes['isOpen']?.currentValue === true;
    const retargeted =
      this.isOpen && (changes['orderId'] || changes['subscriptionId'] || changes['intent']);
    if (opened || retargeted) this.load();
  }

  ngOnDestroy(): void {
    // Give the hold back before the subjects close, or the order stays claimed
    // until the window runs out with nobody on the other end of it.
    this.releaseHold();
    this.reload$.next();
    this.reload$.complete();
    this.holdStopped$.next();
    this.holdStopped$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----- CWS-1947: the cancellation window -----

  /** "0:58", for the countdown chip. */
  get holdCountdown(): string {
    return formatCountdown(this.holdSecondsLeft);
  }

  /** The window ran out while the panel was open; Confirm is no longer honoured. */
  get holdExpired(): boolean {
    return Boolean(this.hold) && this.holdSecondsLeft <= 0;
  }

  /** Only shown while there is a live claim worth counting down. */
  get showHoldCountdown(): boolean {
    return Boolean(this.hold) && this.state === 'select' && this.holdSecondsLeft > 0;
  }

  /** Under ten seconds the chip turns urgent, so the operator sees it going. */
  get holdRunningOut(): boolean {
    return this.showHoldCountdown && this.holdSecondsLeft <= 10;
  }

  /**
   * Claim the order so an auto-completion mid-decision cannot invalidate the
   * Confirm. Best-effort by design: a refusal leaves the panel working as it did
   * before rather than blocking a cancel over a claim the operator never asked for.
   */
  private takeHold(): void {
    this.cancelRefundService
      .takeHold({
        orderId: this.orderId,
        subscriptionId: this.subscriptionId,
        locationId: this.locationId,
      })
      .pipe(
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        catchError(err => of(this.errorResponse(err)))
      )
      .subscribe(res => {
        if (!res.status || !res.data) {
          // Worth saying out loud: another till is on this same order. Everything
          // else is silent -- the cancel still works, just without a window.
          if ((res as CancelRefundErrorBody).code === 'held_elsewhere') {
            this.errorMessage = ERROR_COPY['held_elsewhere'];
          }
          return;
        }
        this.hold = res.data;
        this.startHoldCountdown();
      });
  }

  /**
   * Tick against the server's `expires_at` rather than decrementing a counter:
   * a backgrounded tab or a sleeping tablet resumes showing the real remaining time,
   * not whatever it had before it stopped getting frames.
   */
  private startHoldCountdown(): void {
    this.holdStopped$.next();
    this.holdSecondsLeft = holdRemainingSeconds(this.hold?.expires_at);
    // Outside Angular's zone on purpose. A one-second ticker inside it leaves the
    // zone permanently unstable, so anything awaiting whenStable -- which is most
    // of this component's own specs -- waits forever. Re-entering per tick keeps
    // change detection for the one field that actually moved.
    this.zone.runOutsideAngular(() => {
      interval(1000)
        .pipe(takeUntil(this.holdStopped$), takeUntil(this.reload$), takeUntil(this.destroy$))
        .subscribe(() => this.zone.run(() => this.tickHold()));
    });
  }

  /** One second of the window gone; at zero the Confirm stops being offered. */
  private tickHold(): void {
    this.holdSecondsLeft = holdRemainingSeconds(this.hold?.expires_at);
    if (this.holdSecondsLeft <= 0) {
      this.holdStopped$.next();
      this.errorMessage = ERROR_COPY['hold_expired'];
    }
  }

  /** Fire and forget: an unreleased hold expires on its own, so nothing waits on this. */
  private releaseHold(): void {
    const holdId = this.hold?.hold_id;
    this.hold = null;
    this.holdSecondsLeft = 0;
    this.holdStopped$.next();
    if (!holdId) return;
    this.cancelRefundService
      .releaseHold(holdId)
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  // ----- what is being actioned -----

  get action(): CancelRefundAction {
    return this.items.length ? actionFor(this.items) : 'none';
  }

  get isRefund(): boolean {
    return this.action === 'refund';
  }

  get isMembership(): boolean {
    return !!this.options?.membership && this.items.some(item => item.kind === 'package');
  }

  get isPrepaid(): boolean {
    return !!this.options?.prepaid && this.items.some(item => item.kind === 'package');
  }

  /** "Ultimate Wash + Tire Shine" / "Unlimited Monthly" */
  get label(): string {
    return targetLabel(this.items);
  }

  /** "Refund Ultimate Wash" / "Cancel membership" / "Cancel prepaid" */
  get title(): string {
    if (this.state === 'unavailable' && !this.items.length) {
      return this.intent === 'refund' ? 'Refund' : 'Cancel';
    }
    if (this.isRefund) return `Refund ${this.label}`;
    if (this.family) return 'Cancel family plan';
    if (this.isMembership) return 'Cancel membership';
    if (this.isPrepaid) return 'Cancel prepaid';
    return `Cancel ${this.label}`;
  }

  get subtitle(): string {
    const parts = [
      this.options?.order_number || this.orderNumber
        ? `Order ${this.options?.order_number || this.orderNumber}`
        : null,
      this.customerName,
      this.orderDate,
    ].filter(Boolean);
    return parts.join(' · ');
  }

  /** The item card's second line. */
  get itemSubline(): string {
    if (this.isRefund) return "Refund only · can't be cancelled";
    if (this.isMembership) {
      const renews = formatDate(this.options?.membership?.current_period_end);
      return renews ? `Cancel membership · renews ${renews}` : 'Cancel membership';
    }
    if (this.isPrepaid) {
      const p = this.options!.prepaid!;
      return `${p.remaining_washes} of ${p.total_washes} washes left`;
    }
    return 'Cancel';
  }

  /**
   * CWS-1833: a prepaid book lists its washes one per line, as the design shows
   * ("Champion · 2 of 5 washes left"); a book with one line, or an older
   * svc-order that sends none, falls back to the single total above.
   */
  get itemSublines(): string[] {
    const washes = this.isPrepaid && !this.isRefund ? (this.options?.prepaid?.washes ?? []) : [];
    if (washes.length < 2) {
      return [this.itemSubline];
    }
    return washes.map(wash => {
      const name = (wash.name || '').trim() || 'Wash';
      return `${name} · ${wash.remaining} of ${wash.total} washes left`;
    });
  }

  /** "$29.99/mo" for a membership, the paid amount otherwise. */
  get itemAmount(): string {
    if (this.isMembership) {
      const cents = this.options?.membership?.last_charge_cents || this.itemCents;
      return `${formatCents(cents)}/mo`;
    }
    return formatCents(this.itemCents);
  }

  private get itemCents(): number {
    return this.items.reduce((sum, item) => sum + Math.max(0, item.amount_cents || 0), 0);
  }

  get stateLabel(): string {
    return this.isRefund ? 'Completed' : 'Active';
  }

  /** The design's blue note on a completed item. */
  get completedNote(): string | null {
    if (!this.isRefund) return null;
    return `${this.label} is already completed, so it can't be cancelled. Issue a full or partial refund instead.`;
  }

  /** "Customer keeps washing until Nov 18." */
  /** The design's "When" tiles, membership only. */
  get showWhen(): boolean {
    if (this.family) return !!this.familySelection.mode;
    return this.isMembership && !this.isRefund;
  }

  get periodEndNote(): string {
    // Each plate of a family renews on its own date.
    if (this.family && this.familyPlates.length > 1) {
      return 'Each plate keeps washing until the end of its paid period.';
    }
    const ends = formatDate(this.options?.membership?.current_period_end);
    return ends
      ? `Customer keeps washing until ${ends}.`
      : 'Customer keeps washing until the end of the paid period.';
  }

  readonly immediateNote = 'Membership ends today.';

  pickTiming(timing: CancelTiming): void {
    if (this.locked) return;
    this.selection.cancelTiming = timing;
  }

  /** "Ends Nov 18" / "Ends today" */
  private get endsLabel(): string {
    if (this.selection.cancelTiming === 'immediate') return 'Ends today';
    const ends = formatDate(this.options?.membership?.current_period_end);
    return ends ? `Ends ${ends}` : 'Ends at period end';
  }

  // ----- refund -----

  get refundOptions(): RefundOption[] {
    return targetOptions(this.items);
  }

  get amounts() {
    return targetAmounts(this.items, this.options?.refundable_cents ?? 0);
  }

  optionAmount(option: RefundOption): string | null {
    const amounts = this.amounts;
    if (option === 'none') return null;
    if (option === 'custom') return `Up to ${formatCents(amounts.custom.max_cents)}`;
    if (option === 'prorated') {
      const basis = proratedBasisLabel(this.items[0]);
      return `${formatCents(amounts.prorated ?? 0)}${basis ? ` · ${basis}` : ''}`;
    }
    return formatCents(amounts.full);
  }

  get customMaxLabel(): string {
    return `Max ${formatCents(this.amounts.custom.max_cents)}`;
  }

  get customProblem(): string | null {
    if (this.selection.choice.option !== 'custom') return null;
    if (!this.selection.choice.customInput) return null;
    return (
      amountForChoice(this.items, this.options?.refundable_cents ?? 0, this.selection.choice)
        .problem?.message ?? null
    );
  }

  pickOption(option: RefundOption): void {
    if (this.locked) return;
    this.selection.choice = { ...this.selection.choice, option };
  }

  setCustomInput(value: string): void {
    if (this.locked) return;
    this.selection.choice = { ...this.selection.choice, customInput: value };
  }

  /** The refund the Confirm would send. */
  get refundCents(): number {
    if (this.family) return familyRefundTotal(this.familyPlates, this.familySelection.choices);
    if (!this.options) return 0;
    const { cents, problem } = amountForChoice(
      this.items,
      this.options.refundable_cents,
      this.selection.choice
    );
    return problem ? 0 : cents;
  }

  get isCash(): boolean {
    if (this.family) return familyNeedsCash(this.familyPlates, this.familySelection.choices);
    return this.options?.refund_method === 'cash';
  }

  // ----- family (CWS-1579) -----

  get familyModes(): FamilyCancelMode[] {
    return FAMILY_MODES.filter(mode => this.family?.modes.includes(mode));
  }

  get familyPlates(): FamilyCancelPlate[] {
    return platesInScope(this.family, this.familySelection.mode);
  }

  get familyEndingPlates(): FamilyCancelPlate[] {
    return endingPlates(this.family, this.familySelection.mode);
  }

  get familyEndingNote(): string | null {
    const ending = this.familyEndingPlates;
    if (!ending.length) return null;
    return `${ending.map(plate => plateLabel(plate)).join(', ')} already ending; leaves the family too.`;
  }

  get familyModeTiles(): FamilyModeTile[] {
    return familyModeTiles(this.family, this.familyModes);
  }

  get newPrimaryOptions(): NewPrimaryOption[] {
    return newPrimaryOptions(this.family);
  }

  get needsNewPrimary(): boolean {
    return needsNewPrimary(this.family, this.familySelection.mode);
  }

  get primaryOnlyNote(): string | null {
    return this.familySelection.mode === 'primary_only' ? primaryOnlyNote(this.family) : null;
  }

  pickFamilyMode(mode: FamilyCancelMode): void {
    if (this.locked || familyModeBlockReason(this.family, mode)) return;
    // With only one member able to take over, they are chosen for staff.
    const newPrimaryMemberId =
      this.familySelection.newPrimaryMemberId ??
      (mode === 'primary_only' ? defaultNewPrimary(this.family) : null);
    this.familySelection = { ...this.familySelection, mode, newPrimaryMemberId };
  }

  pickNewPrimary(memberId: string | null): void {
    if (this.locked) return;
    this.familySelection = { ...this.familySelection, newPrimaryMemberId: memberId || null };
  }

  plateLabel(plate: FamilyCancelPlate): string {
    return plateLabel(plate);
  }

  plateRole(plate: FamilyCancelPlate): string {
    return plateRoleLabel(plate);
  }

  plateOptions(plate: FamilyCancelPlate): { option: RefundOption; label: string }[] {
    return plateOptionLabels(plate);
  }

  plateOption(plate: FamilyCancelPlate): RefundOption {
    return this.familySelection.choices[plate.subscription_id]?.option ?? 'none';
  }

  plateCustomInput(plate: FamilyCancelPlate): string {
    return this.familySelection.choices[plate.subscription_id]?.customInput ?? '';
  }

  plateIsCustom(plate: FamilyCancelPlate): boolean {
    return this.plateOption(plate) === 'custom';
  }

  plateProblem(plate: FamilyCancelPlate): string | null {
    if (!this.plateIsCustom(plate) || !this.plateCustomInput(plate)) return null;
    return plateRefund(plate, this.familySelection.choices[plate.subscription_id]).problem;
  }

  plateRefundText(plate: FamilyCancelPlate): string {
    const { cents } = plateRefund(plate, this.familySelection.choices[plate.subscription_id]);
    return cents > 0 ? `Refund ${formatCents(cents)}` : 'No refund';
  }

  pickPlateOption(plate: FamilyCancelPlate, option: RefundOption): void {
    this.setPlateChoice(plate, { option, customInput: '' });
  }

  setPlateCustomInput(plate: FamilyCancelPlate, value: string): void {
    this.setPlateChoice(plate, { option: 'custom', customInput: value });
  }

  private setPlateChoice(
    plate: FamilyCancelPlate,
    choice: { option: RefundOption; customInput: string }
  ): void {
    if (this.locked) return;
    this.familySelection = {
      ...this.familySelection,
      choices: { ...this.familySelection.choices, [plate.subscription_id]: choice },
    };
  }

  trackPlate(_index: number, plate: FamilyCancelPlate): string {
    return plate.subscription_id;
  }

  /** The tiles are rebuilt by a getter on every pass; without this the DOM is too, and a click is lost. */
  trackTile(_index: number, tile: FamilyModeTile): string {
    return tile.mode;
  }

  trackPlateOption(_index: number, entry: { option: RefundOption }): RefundOption {
    return entry.option;
  }

  trackNewPrimary(_index: number, option: NewPrimaryOption): string {
    return option.plate.member_id;
  }

  // ----- reason -----

  get reasons(): CancelRefundReason[] {
    return this.options ? reasonsFor(this.options, this.action) : [];
  }

  get reasonPrompt(): CancelRefundReasonPrompt | null {
    return this.options ? promptFor(this.options, this.action) : null;
  }

  get reasonLabel(): string {
    return this.isRefund
      ? 'Refund reason'
      : this.reasonPrompt?.title || 'Why is the customer cancelling?';
  }

  get noteRequired(): boolean {
    return this.selection.reasonCode === REASON_REQUIRING_NOTE;
  }

  pickReason(code: string): void {
    if (this.locked) return;
    this.selection.reasonCode = code || null;
  }

  // ----- summary -----

  /** "Refund $24.00" / "Ends Nov 18 · no refund" / "Cancelled · no refund" */
  get summaryOutcome(): string {
    const refund = this.refundCents;
    const refundText = refund > 0 ? `Refund ${formatCents(refund)}` : 'No refund';
    if (this.isRefund) return refundText;
    const tail = refund > 0 ? `refund ${formatCents(refund)}` : 'no refund';
    if (this.isMembership) return `${this.endsLabel} · ${tail}`;
    return `Cancelled · ${tail}`;
  }

  get summaryTotalLabel(): string {
    return this.options
      ? `Total refund to ${refundDestinationLabel(this.options)}`
      : 'Total refund';
  }

  // ----- confirm -----

  get validation(): { ok: boolean; reason: string | null } {
    if (this.locked) return { ok: true, reason: null };
    // CWS-1947: past the window the server will refuse, so do not offer the tap.
    if (this.holdExpired) return { ok: false, reason: ERROR_COPY['hold_expired'] };
    if (this.family) {
      return validateFamily(this.family, this.familySelection, this.selection);
    }
    return this.options
      ? validateSelection(this.options, this.items, this.selection)
      : { ok: false, reason: null };
  }

  /** "Refund $24.00" / "Cancel membership" / "Confirm" while a custom amount is blank. */
  get confirmLabel(): string {
    if (this.locked) return 'Finish';
    if (this.family) {
      return familyConfirmLabel(
        this.familySelection.mode,
        this.familyPlates.length,
        this.refundCents
      );
    }
    if (this.selection.choice.option === 'custom' && this.refundCents <= 0) return 'Confirm';
    if (this.isRefund) return `Refund ${formatCents(this.refundCents)}`;
    // The design: "Refund $29.99 & cancel" once a refund rides on the cancel.
    if (this.refundCents > 0) return `Refund ${formatCents(this.refundCents)} & cancel`;
    if (this.isMembership) return 'Cancel membership';
    if (this.isPrepaid) return 'Cancel prepaid';
    return `Cancel ${this.label}`;
  }

  close(): void {
    if (this.state === 'processing') return;
    this.releaseHold();
    this.closed.emit();
  }

  confirm(): void {
    this.staleRetried = false;
    this.submit();
  }

  private submit(): void {
    if (!this.options || this.state !== 'select' || !this.validation.ok) return;
    if (this.family) {
      this.submitFamily(this.family);
      return;
    }
    const body =
      this.pendingBody ??
      buildRequest(this.options, this.items, this.selection, this.requestId, {
        locationId: this.locationId,
        orderId: this.orderId,
        subscriptionId: this.subscriptionId,
        holdId: this.hold?.hold_id ?? null,
      });
    this.state = 'processing';
    this.errorMessage = null;
    this.cancelRefundService
      .execute(body)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => of(this.errorResponse(err)))
      )
      .subscribe(res => {
        if (!res.status || !res.data) {
          const refusal = res as CancelRefundErrorBody;
          // The server answered that it refused (4xx): nothing was written, and
          // a new id keeps a corrected retry from being deduped. A timeout, a
          // network error or a 5xx may have been processed anyway, so the SAME
          // id is kept: a resubmit then replays rather than refunds twice.
          if (this.wasRefused(refusal)) {
            this.requestId = newRequestId();
            this.pendingBody = null;
          }
          if (refusal.code === 'changed_refresh' && !this.staleRetried) {
            this.staleRetried = true;
            this.refreshAfterStale();
            return;
          }
          this.state = 'select';
          this.errorMessage = this.copyFor(refusal);
          return;
        }
        // CWS-1579: the membership is cancelled but its family was not updated. The same
        // request again replays the cancel and finishes the family, so keep it to resend.
        if (familyUpdatePending(res.data)) {
          this.pendingBody = body;
          this.state = 'select';
          this.errorMessage =
            'The membership is cancelled, but the family could not be updated. Select Finish to try again.';
          return;
        }
        this.done.emit({
          result: res.data,
          orderId: this.options?.order_id ?? this.orderId ?? '',
          summary: this.summaryFor(res.data),
        });
      });
  }

  /**
   * CWS-1846: the server's version token moved under an open panel. Reload the
   * options; when they still offer exactly what is on screen, resend the same
   * selection with the fresh token and the operator never sees anything. When an
   * amount or an item did change, show the new figures and ask them to confirm again.
   */
  private refreshAfterStale(): void {
    const before = this.options;
    const retrying = this.items;
    if (!before) {
      this.state = 'select';
      return;
    }
    this.cancelRefundService
      .getOptions({
        orderId: this.orderId,
        subscriptionId: this.subscriptionId,
        locationId: this.locationId,
      })
      .pipe(
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        catchError(err => of(this.errorResponse(err)))
      )
      .subscribe(res => {
        if (!res.status || !res.data) {
          this.state = 'select';
          this.errorMessage = this.copyFor(res as CancelRefundErrorBody);
          return;
        }
        this.options = res.data;
        this.items = targetItems(res.data.items, this.intent);
        if (sameOffer(before, res.data, this.intent) && this.items.length === retrying.length) {
          this.state = 'select';
          this.submit();
          return;
        }
        this.state = 'select';
        this.errorMessage = ERROR_COPY['changed_refresh'];
        if (!this.items.length) {
          this.state = 'unavailable';
          this.errorMessage = unavailableCopy(res.data, this.intent);
          return;
        }
        // The reload may have withdrawn the option that is still selected; fall
        // back to the default so the operator confirms from a live choice.
        if (!targetOptions(this.items).includes(this.selection.choice.option)) {
          this.selection.choice = { option: defaultOption(this.items), customInput: '' };
        }
      });
  }

  /**
   * CWS-1579: Cancel all / Cancel primary only. A 4xx refusal changed nothing, so a new id lets
   * a corrected retry through; anything else keeps the id, and the plates are cancelled once.
   */
  private submitFamily(family: FamilyCancelOptions): void {
    const body =
      this.pendingFamilyBody ??
      buildFamilyRequest({
        family,
        selection: this.familySelection,
        shared: this.selection,
        requestId: this.requestId,
        locationId: this.locationId,
      });
    this.state = 'processing';
    this.errorMessage = null;
    this.cancelRefundService
      .executeFamily(body)
      .pipe(
        takeUntil(this.destroy$),
        catchError(err => of(this.errorResponse(err)))
      )
      .subscribe(res => {
        if (!res.status || !res.data) {
          const refusal = res as CancelRefundErrorBody;
          if (this.wasRefused(refusal)) {
            this.requestId = newRequestId();
            this.pendingFamilyBody = null;
          }
          this.state = 'select';
          this.errorMessage = this.copyFor(refusal);
          if (refusal.code === 'changed_refresh')
            this.loadFamily(family.members[0].subscription_id);
          return;
        }
        const result = res.data;
        // The plates are cancelled; only the family rows are behind. The same request again
        // replays the cancellations and finishes the family, so keep it to resend.
        if (result.family?.pending && result.plates.some(plate => plate.cancelled)) {
          this.pendingFamilyBody = body;
          this.state = 'select';
          this.errorMessage =
            'The memberships are cancelled, but the family could not be updated. Select Finish to try again.';
          return;
        }
        this.done.emit({
          result: familyDoneResult(result),
          orderId: this.options?.order_id ?? this.orderId ?? '',
          summary: familySummary(result, family),
          familyResult: result,
        });
      });
  }

  /** One line for the host's toast. */
  private summaryFor(result: CancelRefundResult): string {
    const refunded = result.refund?.refunded_cents ?? 0;
    const owed = result.refund?.requested_cents ?? 0;
    if (result.status === 'refund_failed') {
      return `${this.label} ${this.isRefund ? 'was not refunded' : 'is cancelled, but the refund failed'}. ${formatCents(owed)} is still owed — use Retry refund on the row.`;
    }
    if (result.status === 'failed')
      return `${this.label} could not be actioned. Nothing was changed.`;
    const money =
      refunded > 0
        ? result.refund?.method === 'cash'
          ? ` ${formatCents(refunded)} refunded in cash.`
          : ` ${formatCents(refunded)} is on its way back to the customer's card.`
        : '';
    if (this.isRefund) return `${this.label} refunded.${money}`;
    if (this.isMembership) {
      if (this.selection.cancelTiming === 'immediate') {
        return `Membership cancelled; it ended today.${money}`;
      }
      const ends = formatDate(this.options?.membership?.current_period_end);
      return `Membership cancelled${ends ? `; it ends ${ends}` : ''}.${money}`;
    }
    return `${this.label} cancelled.${money}`;
  }

  // ----- helpers -----

  money(cents: number | null | undefined): string {
    return formatCents(cents);
  }

  trackReason(_index: number, reason: CancelRefundReason): string {
    return reason.code;
  }

  private load(): void {
    // A re-open or a re-target drops whatever the previous load still had in
    // flight; a slower answer for the old target must not replace this one.
    this.reload$.next();
    this.releaseHold();
    this.state = 'loading';
    this.options = null;
    this.items = [];
    this.family = null;
    this.familySelection = { mode: null, newPrimaryMemberId: null, choices: {} };
    this.pendingFamilyBody = null;
    this.pendingBody = null;
    this.errorMessage = null;
    this.requestId = newRequestId();
    this.cancelRefundService
      .getOptions({
        orderId: this.orderId,
        subscriptionId: this.subscriptionId,
        locationId: this.locationId,
      })
      .pipe(
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        catchError(err => of(this.errorResponse(err)))
      )
      .subscribe(res => {
        if (!res.status || !res.data) {
          this.state = 'unavailable';
          this.errorMessage = this.copyFor(res as CancelRefundErrorBody);
          return;
        }
        this.options = res.data;
        this.items = targetItems(res.data.items, this.intent);
        if (!this.items.length) {
          this.state = 'unavailable';
          this.errorMessage = unavailableCopy(res.data, this.intent);
          return;
        }
        this.selection = {
          choice: { option: defaultOption(this.items), customInput: '' },
          reasonCode: null,
          reasonNote: '',
          cashHandedOver: false,
          cancelTiming: 'period_end',
        };
        // CWS-1947: only a cancel races the auto-completion. A refund is already
        // acting on a completed order, so there is nothing to hold it against.
        if (this.intent === 'cancel') this.takeHold();
        // CWS-1579: a family primary with members is cancelled through the family flow.
        if (isFamilyPrimaryCancel(res.data, this.intent) && res.data.subscription_id) {
          this.loadFamily(res.data.subscription_id);
          return;
        }
        this.state = 'select';
      });
  }

  /** CWS-1579: every plate of the family, each with its own refund options. */
  private loadFamily(subscriptionId: string): void {
    this.state = 'loading';
    this.cancelRefundService
      .getFamilyOptions(subscriptionId, this.locationId)
      .pipe(
        takeUntil(this.reload$),
        takeUntil(this.destroy$),
        catchError(err => of(this.errorResponse(err)))
      )
      .subscribe(res => {
        if (!res.status || !res.data) {
          this.state = 'unavailable';
          this.errorMessage = this.copyFor(res as CancelRefundErrorBody);
          return;
        }
        const previous = this.family ? this.familySelection : null;
        this.family = res.data;
        this.familySelection = reconcileFamilySelection(previous, res.data);
        this.pendingFamilyBody = null;
        this.state = 'select';
      });
  }

  /** A definitive refusal from the server, as opposed to not knowing whether it ran. */
  private wasRefused(body: CancelRefundErrorBody): boolean {
    const code = Number(body?.status_code ?? 0);
    return code >= 400 && code < 500 && code !== 408 && code !== 429;
  }

  private errorResponse(err: unknown): CancelRefundErrorBody {
    const body = (err as { error?: Partial<CancelRefundErrorBody> })?.error;
    return {
      status: false,
      status_code: Number(body?.status_code ?? (err as { status?: number })?.status ?? 0),
      code: body?.code,
      message: body?.message,
    };
  }

  private copyFor(body: CancelRefundErrorBody | null | undefined): string {
    const code = body?.code ?? '';
    return ERROR_COPY[code] || body?.message || 'Something went wrong. Please try again.';
  }
}
